/**
 * Whether the recorded choice still names something the visitor can be sent to checkout with.
 *
 * The choice is a pair of ids (ADR 0006), and both of them are answers the API gives *to whoever is
 * asking*. `GET /v2/products` prices every product in the caller's currency — the IP's for a
 * signed-out visitor, `user.stripe.currency` for a returning customer — and drops any product it
 * cannot price in that currency at all. So the same list, asked for twice either side of a sign-in,
 * can come back with a different price against the product, or without the product on it.
 *
 * Either way the pick is stale, and for the same reason: it was made against a list that was
 * answering a different question. There is nothing to repair here — a price cannot be substituted
 * without charging a total nobody agreed to, and a missing product cannot be substituted at all —
 * so the only honest answer is to put the visitor back in front of the list. The API reaches the
 * same conclusion about the currency case on its own (`checkPriceEligibility`, ADR 0002 in
 * zl-api); this check is what catches it before a checkout tab has been opened for nothing.
 *
 * Three things have to be true, and the third is the one that is easy to miss: an answer that has
 * not arrived is not a disagreement. `offered` is `undefined` both while the list is in flight and
 * when the list has come back without the product, and only `answered` tells those apart. Treating
 * the first as the second would bounce every deferred checkout during its own refetch.
 */
// eslint-disable-next-line import/prefer-default-export
export const isChoiceStale = ({
  recorded,
  offered,
  answered,
}: {
  /** The price id the visitor picked, as the machine recorded it. */
  recorded: string | undefined;
  /** The price the product list resolves for that product now, if it still lists it. */
  offered: string | undefined;
  /** Whether the product list has answered at all. */
  answered: boolean;
}) => !!recorded && answered && recorded !== offered;
